import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography, Fade } from '@mui/material';
import { 
  Hand as HandIcon,
  Flame as FlameIcon,
  Bot as BotIcon,
  Star as StarIcon,
  Share2 as ShareIcon,
} from 'lucide-react';

function NavbarTitle({ user, profile, resultsCount, suggestedTitles, isPro }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [show, setShow] = useState(true);
  
  // Build messages based on context
  const messages = [];
  
  // 1. Always - brand anchor
  messages.push({ text: "Jobs Jobs Jobs", Icon: null });
  
  // 2. Greeting - signed in shows name, signed out prompts sign-in
  if (user) {
    const firstName = profile?.name?.split(' ')[0] || user.firstName || 'there';
    messages.push({ text: `Hey ${firstName}!`, Icon: HandIcon });
  } else {
    messages.push({ text: "Hi, what's your name?", Icon: HandIcon });
  }
  
  // 3. Results count - whenever there are results
  if (resultsCount > 0) {
    messages.push({ text: `${resultsCount.toLocaleString()} jobs found!`, Icon: FlameIcon });
  }
  
  // 4. AI titles - whenever profile has suggested titles
  if (suggestedTitles?.length > 0) {
    messages.push({ text: `${suggestedTitles.length} AI titles ready!`, Icon: BotIcon });
  }
  
  // 5. Conversion / upgrade messaging
  if (!isPro) {
    messages.push({
      text: user ? "Go Pro for Unlimited" : "Create Free Account Today",
      Icon: StarIcon,
    });
  }
  
  // 6. Share - anytime
  messages.push({ text: "Tell a friend - click share!", Icon: ShareIcon });
  
  // Rotate with fade animation
  useEffect(() => {
    if (messages.length <= 1) return;
    
    let timeoutId;
    const interval = setInterval(() => {
      setShow(false); // Fade out
      timeoutId = setTimeout(() => {
        setCurrentIndex(i => (i + 1) % messages.length);
        setShow(true); // Fade in
      }, 300);
    }, 5000);
    
    return () => {
      clearInterval(interval);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [messages.length]);
  
  const current = messages[currentIndex] || messages[0];
  
  return (
    <Box 
      component={Link} 
      to="/" 
      sx={{ 
        textDecoration: 'none', 
        color: 'inherit', 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        minWidth: { xs: 140, sm: 200 }, // Prevent layout shift
      }}
    >
      <Box
        component="img"
        src="/logo.svg"
        alt="Jobs Jobs Jobs"
        sx={{ width: 32, height: 32 }}
      />
      <Fade in={show} timeout={300}>
        <Typography 
          variant="h6" 
          component="h1" 
          fontWeight="bold"
          sx={{ 
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: { xs: '0.95rem', sm: '1.25rem' },
          }}
        >
          {current.Icon && <current.Icon size={20} />}
          {current.text}
        </Typography>
      </Fade>
    </Box>
  );
}

export default NavbarTitle;
